/**
 * Class SimpleShowAll allows for revealing all hidden content dynamically across separate components, linked by identifiers rather than DOM hierarchy.
 * This class simplifies the process of displaying all hidden elements within any part of the webpage with a single click.
 *
 * How it works:
 * - Each component (either a container or a button) is identified by a unique ID.
 * - The class expects:
 *   1. A container identified by `data-simple-show-all-container="some_id"` which holds content, initially hidden with the `hidden` CSS class.
 *   2. A button identified by `data-simple-show-all-button="some_id"` used to reveal all hidden content in the associated container.
 * - When the show all button is clicked, all hidden elements within the container are revealed.
 * - After revealing all elements, the show all button is hidden to prevent further interaction.
 *
 * Usage:
 * - To initialize the show all functionality, ensure all components are marked with appropriate data attributes and call `SimpleShowAll.init()` after the document content has loaded.
 */
export default class SimpleShowAll {
  constructor(container, button, hiddenClass) {
    this.showAllContainer = container;
    this.showAllButton = button;
    this.hiddenClass = hiddenClass;
    this.showAllButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.showAll();
    });
  }

  showAll() {
    let hiddenElements = this.showAllContainer.querySelectorAll(
      this.hiddenClass
    );
    hiddenElements.forEach((element) =>
      element.classList.remove(this.hiddenClass)
    );
    this.showAllButton.style.display = "none";
  }

  static init() {
    const containers = document.querySelectorAll(
      "[data-simple-show-all-container]"
    );
    containers.forEach((container) => {
      const id = container.getAttribute("data-simple-show-all-container");
      const button = document.querySelector(
        `[data-simple-show-all-button="${id}"]`
      );
      const hiddenClass =
        container.getAttribute("data-simple-show-more-hidden-class") ||
        "hidden";

      if (container && button) {
        new SimpleShowAll(container, button, hiddenClass);
      }
    });
  }
}
